"use client";

import { orpc } from "@shared/lib/orpc";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@ui/components/button";
import { Field, FieldLabel } from "@ui/components/field";
import { DisabledReason } from "@ui/components/info-tip";
import { Input } from "@ui/components/input";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetTitle,
} from "@ui/components/sheet";
import { Spinner } from "@ui/components/spinner";
import { toast } from "@ui/components/toast";
import { cn } from "@ui/lib";
import { useState } from "react";
import { keyedLines, money, orderName } from "./meta";
import {
	actionFailed,
	QuantityStepper,
	ShopperGets,
	useOrderUpdated,
} from "./OrderActions";
import type { OrderDetail } from "./types";

/**
 * Change quantities or remove lines (0) before the order leaves the branch. Lines without a
 * catalog id (typed in by a retailer's system) cannot be matched back, so they stay as they are.
 */
export function LineEditor({
	order,
	organizationId,
	open,
	onOpenChange,
}: {
	order: OrderDetail;
	organizationId: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const [quantities, setQuantities] = useState<Record<string, number>>({});
	const [note, setNote] = useState("");
	const updated = useOrderUpdated(organizationId);
	const save = useMutation(
		orpc.orders.editLines.mutationOptions({
			onSuccess: async (next) => {
				toast.add({
					type: "success",
					title: `Items of order ${orderName(next)} changed`,
				});
				setQuantities({});
				setNote("");
				onOpenChange(false);
				await updated(next);
			},
			onError: (error) =>
				actionFailed(error, "Couldn't change the items"),
		}),
	);
	const changes = order.lines.flatMap((line) => {
		if (!line.catalogId) {
			return [];
		}
		const next = quantities[line.catalogId];
		return next !== undefined && next !== line.quantity
			? [{ catalogId: line.catalogId, quantity: next }]
			: [];
	});

	return (
		<Sheet
			open={open}
			onOpenChange={(next) => {
				if (!next) {
					setQuantities({});
				}
				onOpenChange(next);
			}}
		>
			<SheetContent
				side="right"
				className="gap-0 p-0 data-[side=right]:w-full data-[side=right]:sm:max-w-md"
			>
				<SheetHeader className="border-border/70 border-b pr-12">
					<SheetTitle>
						Edit items of order {orderName(order)}
					</SheetTitle>
					<SheetDescription>
						Set a line to 0 to remove it. The shopper is told what
						changed.
					</SheetDescription>
				</SheetHeader>
				<div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
					<ul className="flex flex-col divide-y divide-border/70">
						{keyedLines(order.lines).map(({ key, line }) => {
							const value = line.catalogId
								? (quantities[line.catalogId] ?? line.quantity)
								: line.quantity;
							const changed = value !== line.quantity;
							return (
								<li
									key={key}
									className="flex items-center gap-3 py-2.5"
								>
									<div className="flex min-w-0 flex-1 flex-col">
										<span
											dir="auto"
											className={cn(
												"truncate text-left text-sm",
												value === 0 &&
													"text-muted-foreground line-through",
											)}
										>
											{line.title}
										</span>
										<span className="text-muted-foreground text-xs tabular">
											{money(
												line.unitPrice,
												order.currency,
											)}{" "}
											each
											{changed
												? ` · was ${line.quantity}`
												: ""}
										</span>
									</div>
									<DisabledReason
										reason={
											!line.catalogId &&
											"This line came without a catalog id, so it can't be changed here."
										}
									>
										<QuantityStepper
											label={line.title}
											value={value}
											disabled={!line.catalogId}
											onChange={(next) =>
												line.catalogId &&
												setQuantities((prev) => ({
													...prev,
													[line.catalogId as string]:
														next,
												}))
											}
										/>
									</DisabledReason>
								</li>
							);
						})}
					</ul>
					<Field>
						<FieldLabel htmlFor="edit-note">
							Why (sent to the shopper)
						</FieldLabel>
						<Input
							id="edit-note"
							value={note}
							onChange={(e) => setNote(e.target.value)}
							placeholder="Eggs 30 pcs is out of stock today"
							maxLength={500}
						/>
					</Field>
					{open && changes.length > 0 ? (
						<ShopperGets
							organizationId={organizationId}
							orderId={order.id}
							action="edit_lines"
						/>
					) : null}
				</div>
				<SheetFooter className="border-border/70 border-t">
					<DisabledReason
						reason={
							changes.length === 0 && "Change a quantity first"
						}
					>
						<Button
							className="w-full"
							onClick={() =>
								save.mutate({
									organizationId,
									orderId: order.id,
									lines: changes,
									...(note.trim()
										? { note: note.trim() }
										: {}),
								})
							}
							disabled={changes.length === 0 || save.isPending}
						>
							{save.isPending ? (
								<Spinner data-icon="inline-start" />
							) : null}
							{changes.length === 0
								? "Save changes"
								: `Save ${changes.length} change${changes.length === 1 ? "" : "s"}`}
						</Button>
					</DisabledReason>
				</SheetFooter>
			</SheetContent>
		</Sheet>
	);
}
